import {
  createRouter,
  createWebHistory,
  createMemoryHistory,
  RouterView,
} from "vue-router";
import { getConfigOfCustomEditor } from "./data/customEditor";
import { stripLanguagePrefix } from "./util/dataProvider";
import { useMockupStore } from "@/datastore/module/mockup.js";
import { use3dMockupStore } from "@/datastore/module/3dmockups.js";
import { categoriesList } from "@/util/common.util";
import TagsCategoryMap from "@/data/tags_category_map.json";
import Error404Page from "@/components/404Error.vue";

const Editor = () => import("@/views/mockup/editor/index.vue");
const Landing = () => import("@/views/landing/index.vue");
const Listing = () => import("./views/mockup/listing/index.vue");
const _3dListing = () => import("./views/3d/listing/index.vue");
const Category = () => import("./components/category/index.vue");
const Pricing = () => import("./views/pricing/index.vue");
const Login = () => import("@/views/login/index.vue");
const ImageEditor = () => import("./components/ImageEditor.vue");
const CustomMockup = () => import("./views/custom-mockup/index.vue");
const _3dMockupEditor = () => import("@/views/3d/editor/index.vue");
const Searching = () => import("./components/Searching.vue");
const MockupLanding = () => import("./components/MockupLanding.vue");
const MockupApiLanding = () => import("./components/MockupApiLanding.vue");
const VideoMockupListing = () => import("./components/videoMockup/index.vue");
const VideoEditor = () => import("./components/videoMockup/VideoEditor.vue");
const Profile = () => import("./components/profile/index.vue");

export const routes = [
  {
    path: "/:lang?",
    component: RouterView,
    children: [
      { name: "home", path: "", component: Landing },
      { name: "login", path: "login", component: Login },
      { name: "pricing", path: "pricing", component: Pricing },
      { name: "mockup-editor", path: "mockup/edit/:id", component: Editor },
      {
        name: "3dmockup-editor",
        path: "3d/edit/:id",
        component: _3dMockupEditor,
      },
      { name: "landing", path: "landing", component: Landing },
      { name: "mockups", path: "mockups", component: Listing },
      { name: "category-list", path: "category", component: Category },
      {
        name: "3d-listing",
        path: "3d",
        component: _3dListing,
      },
      { name: "category", path: "mockups/:category", component: Listing },
      { name: "image-editor", path: "tools/:id", component: ImageEditor },
      { name: "mockup", path: "mockup", component: MockupLanding },
      {
        name: "mockup-api",
        path: "mockup-api",
        component: MockupApiLanding,
      },
      {
        name: "subcategory",
        path: "mockups/:category/:subcategory",
        component: Listing,
      },
      {
        name: "3d-subcategory",
        path: "3d/:subcategory",
        component: _3dListing,
      },
      {
        name: "custom-mockup",
        path: "tools/custom-mockup",
        component: CustomMockup,
      },
      {
        name: "sticker-maker",
        path: "tools/sticker-maker",
        component: Editor,
        props: { customConfig: getConfigOfCustomEditor("sticker-maker") },
      },
      {
        name: "mockup-tag-list",
        path: "mockups/:tags",
        component: Listing,
      },
      {
        name: "3d-tag-list",
        path: "3d/:tag",
        component: _3dListing,
      },
      { name: "search-page", path: "search", component: Searching },
      {
        name: "video-mockup-listing",
        path: "video-mockups",
        component: VideoMockupListing,
      },
      {
        name: "video-mockup-category",
        path: "video-mockups/:category",
        component: VideoMockupListing,
      },
      {
        name: "video-mockup-subcategory",
        path: "video-mockups/:category/:subcategory",
        component: VideoMockupListing,
      },
      {
        name: "video-mockup-tag-list",
        path: "video-mockups/:category/:subcategory/:tag",
        component: VideoMockupListing,
      },
      {
        name: "video-mockup-editor",
        path: "video-mockups/edit/:id",
        component: VideoEditor,
      },
      {
        name: "profile",
        path: "profile",
        component: Profile,
        meta: { requiresAuth: true },
      },
      {
        name: "error-404",
        path: "/404",
        component: Error404Page,
      },
    ],
  },
];

// -----------------------------------------------------------------------------
// Shared helpers for SPA & SSG
// -----------------------------------------------------------------------------

export function scrollBehavior(to, from, savedPosition) {
  if (to.fullPath === from.fullPath) return;
  if (savedPosition && to.path.startsWith("/mockups")) return savedPosition;
  if (to.hash) {
    return { el: to.hash, behavior: "smooth", top: 40 };
  }
  return { top: 0, behavior: "smooth" };
}

// Re-attach the complex before-each guard so we can reuse it from vite-ssg
export function attachRouterGuards(router) {
  router.beforeEach(async (to, from, next) => {
    const store = useMockupStore();
    const currentLang = from.params.lang || to.params.lang;
    if (to.params.lang === "") {
      // do nothing
    } else if (!to.params.lang && currentLang && currentLang !== "en") {
      next({ ...to, params: { ...to.params, lang: currentLang } });
      return;
    }

    store.updateCurrentRoute(to.name);
    const strippedPath = stripLanguagePrefix(to.path);

    if (strippedPath === "/mockups" || strippedPath === "/mockups/") {
      await store.fetchData(to.name, to?.params);
      next();
    } else if (strippedPath.startsWith("/mockups/")) {
      await store.fetchData(to.name, to?.params);
      const categoryExists = Object.keys(categoriesList)?.includes(
        to.params?.category,
      );
      if (
        !categoryExists &&
        !TagsCategoryMap[to.params.tags || to.params.category]
      ) {
        next("/404");
      }
      if (!categoryExists && to.name !== "mockup-tag-list") {
        next({
          name: "mockup-tag-list",
          params: { tags: to.params.category, lang: to.params.lang },
          query: { ...to.query },
          replace: true,
        });
      } else {
        next();
      }
    } else if (strippedPath === "/3d" || strippedPath === "/3d/") {
      next();
    } else if (strippedPath.startsWith("/3d/")) {
      const store3d = use3dMockupStore();
      const subcategoryList = store3d.subcategories;
      const subcategoryExists =
        Array.isArray(subcategoryList) &&
        subcategoryList.includes(
          to.params?.subcategory?.replace("-mockup", ""),
        );

      if (
        !subcategoryExists &&
        to.name !== "3d-tag-list" &&
        to.name !== "3dmockup-editor"
      ) {
        next({
          name: "3d-tag-list",
          params: { tag: to.params.subcategory, lang: to.params.lang },
          query: { ...to.query },
          replace: true,
        });
      } else {
        next();
      }
    } else if (to.path === "/app" || to.path.startsWith("/app/")) {
      const newPath = to.path.replace("/app", "") || "/";
      if (newPath !== to.path) {
        next({ path: newPath, replace: true });
      } else {
        next();
      }
    } else {
      await store.fetchData(to.name, to?.params);
      next();
    }
  });
}

// -----------------------------------------------------------------------------
// SPA router instance (vite dev / vite build)
// -----------------------------------------------------------------------------

const router = createRouter({
  history: import.meta.env.SSR ? createMemoryHistory() : createWebHistory("/"),
  routes,
  scrollBehavior,
});

export default router;
